// src/admin/manage-users/EditUserPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';

const ROLES = ['normalUser', 'subscribedUser', 'editor', 'marketing', 'order', 'admin'];

const EditUserPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    
    // Use a single state object for the form data
    const [formData, setFormData] = useState({
        email: '',
        displayName: '',
        role: 'normalUser',
        profession: '',
        phone: '',
        address: '',
        twitter: '',
        linkedin: '',
        website: ''
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const fetchUser = async () => {
            if (!userId) return;
            try {
                const userDocRef = doc(db, 'users', userId);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    // Populate the entire form with existing data
                    setFormData(userDoc.data());
                } else {
                    setError("User not found.");
                }
            } catch (err) {
                setError("Failed to fetch user data.");
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [userId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveChanges = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const functions = getFunctions();
            const updateUserByAdmin = httpsCallable(functions, 'updateUserByAdmin');
            // Send the entire formData object to the backend function
            await updateUserByAdmin({ uid: userId, newData: formData });
            setSuccess('User details updated successfully!');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        if (window.confirm(`Are you sure you want to permanently delete user ${formData.email}? This action cannot be undone.`)) {
            setLoading(true);
            setError('');
            try {
                const functions = getFunctions();
                const deleteUserByAdmin = httpsCallable(functions, 'deleteUserByAdmin');
                await deleteUserByAdmin({ uid: userId });
                alert('User deleted successfully.');
                navigate('/admin/manage-users');
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading && !formData.email) return <div className="text-center p-5">Loading...</div>;
    if (error) return <div className="alert alert-danger">{error}</div>;

    return (
        <div>
            <h2 className="mb-4">Edit User: {formData.email}</h2>
            {success && <div className="alert alert-success">{success}</div>}
            <div className="card">
                <div className="card-body">
                    {/* The structure is the same as the Add User form */}
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label htmlFor="email" className="form-label">Email Address</label>
                            <input type="email" id="email" name="email" className="form-control" value={formData.email} disabled />
                        </div>
                    </div>
                     <div className="row">
                        <div className="col-md-6 mb-3">
                            <label htmlFor="displayName" className="form-label">Full Name</label>
                            <input type="text" id="displayName" name="displayName" className="form-control" value={formData.displayName} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="role" className="form-label">User Role</label>
                            <select id="role" name="role" className="form-select" value={formData.role} onChange={handleChange}>
                                {ROLES.map(role => <option key={role} value={role}>{role}</option>)}
                            </select>
                        </div>
                    </div>
                    {/* ... other form fields ... */}
                     <div className="row">
                        <div className="col-md-6 mb-3">
                            <label htmlFor="profession" className="form-label">Profession</label>
                            <input type="text" id="profession" name="profession" className="form-control" value={formData.profession} onChange={handleChange} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="phone" className="form-label">Phone</label>
                            <input type="tel" id="phone" name="phone" className="form-control" value={formData.phone} onChange={handleChange} />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label htmlFor="address" className="form-label">Address</label>
                        <input type="text" id="address" name="address" className="form-control" value={formData.address} onChange={handleChange} />
                    </div>
                    <hr />
                    <h5 className="my-3">Social & Website Links</h5>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label htmlFor="website" className="form-label">Website</label>
                            <input type="url" id="website" name="website" className="form-control" value={formData.website} onChange={handleChange} />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label htmlFor="twitter" className="form-label">Twitter URL</label>
                            <input type="url" id="twitter" name="twitter" className="form-control" value={formData.twitter} onChange={handleChange} />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label htmlFor="linkedin" className="form-label">LinkedIn URL</label>
                            <input type="url" id="linkedin" name="linkedin" className="form-control" value={formData.linkedin} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-3">
                        <button onClick={handleSaveChanges} className="btn btn-primary me-2" disabled={loading}>
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button onClick={() => navigate('/admin/manage-users')} className="btn btn-secondary me-2">
                            Back to List
                        </button>
                        <button onClick={handleDeleteUser} className="btn btn-danger" disabled={loading}>
                            Delete User
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditUserPage;